import React, { useState, useMemo, useRef, useEffect } from "react";

type Option = {
  name: string;
  value: string;
};

type DropdownProps = {
  name: string;
  options: Option[];
  value: string | string[];
  onChange: (e: { target: { name: string; value: string | string[] } }) => void;
  className?: string;
  searchable?: boolean;
  multiple?: boolean;
  placeholder?: string;
  disabled?: boolean;
};

const Dropdown: React.FC<DropdownProps> = ({
  name,
  options = [],
  value,
  onChange,
  className = "",
  searchable = false,
  multiple = false,
  placeholder = "Select...",
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const filteredOptions = useMemo(() => {
    return options.filter((opt) => {
      const optionName =
        typeof opt.name === "string"
          ? opt.name
          : opt.name !== undefined
          ? String(opt.name)
          : "";
      return optionName.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [searchTerm, options]);

  const handleOptionClick = (val: string) => {
    if (multiple && Array.isArray(value)) {
      const newValue = value.includes(val)
        ? value.filter((v) => v !== val)
        : [...value, val];
      onChange({ target: { name, value: newValue } });
    } else {
      onChange({ target: { name, value: val } });
      setIsOpen(false);
    }
  };

  const isSelected = (val: string): boolean => {
    return multiple && Array.isArray(value)
      ? value.includes(val)
      : value === val;
  };

  const displayText = () => {
    if (multiple && Array.isArray(value)) {
      return value.length > 0
        ? options
            .filter((opt) => value.includes(opt.value))
            .map((opt) => opt.name)
            .join(", ")
        : placeholder;
    } else {
      return options.find((opt) => opt.value === value)?.name || placeholder;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`border rounded px-3 py-2 bg-white ${
          disabled
            ? "cursor-not-allowed text-gray-400 bg-gray-100 border-gray-200"
            : "cursor-pointer border-gray-300"
        } ${className}`}
        onClick={() => {
          if (disabled) return;
          setIsOpen((prev) => !prev);
        }}
        aria-disabled={disabled}
      >
        <span className="flex justify-between items-center gap-2">
          <span className="flex-1 truncate text-left">{displayText()}</span>
          <svg
            className={`w-4 h-4 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-10 mt-1 w-full bg-white border rounded max-h-60 overflow-auto shadow-lg">
          {searchable && (
            <input
              type="text"
              placeholder="Search..."
              className="w-full p-2 border-b outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          )}
          {filteredOptions.map((opt) => (
            <div
              key={opt.value}
              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                isSelected(opt.value) ? "bg-gray-200 font-medium" : ""
              }`}
              onClick={() => handleOptionClick(opt.value)}
            >
              {opt.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
